import React, {useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import {useForm} from 'react-hook-form'
import {ExclamationCircleIcon, LocationIcon} from '../../../components/icons'
import LoadingOverlay from '../../../components/loading-overlay'
import useDeliveryBooking from '../../../hooks/use-delivery-booking'
import {useBasketStore, useCustomerStore, usePreBookStore} from '../../../store/hooks/useStore'
import {Box, Flex, Stack, Text, useDisclosure} from '../../../vanilla'
import {EditPostCode} from './edit-post-code'
import {YourDeliverySavedAddresses} from './your-delivery-saved-addresses'
import {YourDeliverySelectedAddress} from './your-delivery-selected-address'
import {EditAddressForm} from './edit-address-form'
import {AddAddressForm} from './add-address-form'
import * as styles from '../styles.css'
import {ChangingAddressWarningModal} from './changing-address-warning-modal'
import {noop} from '../../../utils/utils'
import {AddressFormType, OrderAddress} from '../../../types/store/basket'
import {CheckPostalCodeFormFields} from '../../../types/forms'
import {useSoftLogoutData} from '../../../hooks/use-soft-logout-data'
import useDeliveryPreBooking from '../../../hooks/use-delivery-pre-booking'

export const YourDelivery = observer(({preBook = false}) => {
  const {
    deliveryPostalCode,
    basket,
    addressDisplay,
    updateBasketDeliveryNote,
    shippingAddress,
    currentDeliverySlotId,
    isAppliedPostCodeDeliverable,
    deliveryAddressFormExpanded,
    deliveryNotes: deliveryNotesValue    
  } = useBasketStore()
  const { customerInfo, hasSavedAddresses, addresses, primaryAddress } = useCustomerStore()
  const { preBookAddress, setPreBookAddress } = usePreBookStore()
  const softLogoutData = useSoftLogoutData()

  const [deliveryNotesLoading, setDeliveryNotesLoading] = useState<boolean>(false)
  const forbiddenCharsRegex = /[()&"'<>\r\n]/g

  const form = useForm<CheckPostalCodeFormFields>({
    defaultValues: {
      postcode: deliveryPostalCode || '',
    },
  })

  const {
    setPostalCode,
    setDeliveryAddress,
    checkDeliveryValidity,
    loading,
    postCodeAvailability,
    setPostCodeAvailability,
  } = useDeliveryBooking()

  const {loadingPreBook, setPreBookPostCode} = useDeliveryPreBooking()

  const changingDeliveryAddressWarningModal = useDisclosure()
  const [changingDeliveryModalSubmitAction, setChangingDeliveryModalAction] = useState<
    (() => void) | null
  >(null)

  const customerHasBookedSlot = Boolean(currentDeliverySlotId)

  useEffect(()=> {
    if (deliveryPostalCode && deliveryPostalCode !== shippingAddress?.postalCode)  {
      const matchingAddress = addresses.find(
          (address) => address.postalCode ===  deliveryPostalCode
        )
        if (matchingAddress) {
          setDeliveryAddress(matchingAddress)
        }
        else{
          setDeliveryAddress({} as OrderAddress)
        }
    }

  }, [deliveryPostalCode])

  const handleSetDeliveryAddress = async (address: OrderAddress) => {
    if (preBook) {
      setPreBookAddress(address)
    } else if (customerHasBookedSlot && isAppliedPostCodeDeliverable) {
      setChangingDeliveryModalAction(() => {
        return () => setDeliveryAddress(address)
      })
      changingDeliveryAddressWarningModal.onOpen()
    } else {
      await setDeliveryAddress(address)
    }
  }

  const selectedPreBookAddress = preBook ? preBookAddress || primaryAddress : null

  const customerAddressSelected =
    preBook && selectedPreBookAddress
      ? selectedPreBookAddress
      : addresses.find((address) => address.addressId === shippingAddress?.c_customerAddressID)

  const handleDeliveryNoteInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    let note = event.currentTarget.value
    if (forbiddenCharsRegex.test(note)) {
      note = note.replace(forbiddenCharsRegex, '')
      event.currentTarget.value = note
    }
  }

  useEffect(() => {
    if (preBook && selectedPreBookAddress) {
      setPreBookAddress(selectedPreBookAddress)
      setPreBookPostCode(selectedPreBookAddress?.postalCode)
    }
  }, [preBook, selectedPreBookAddress])

  return (
    <Box position="relative" backgroundColor="white" gridColumn={['1', '1', 'span 7']}>
      <LoadingOverlay isLoading={loading || !customerInfo || loadingPreBook} />
      <Text
        variant="heading1"
        paddingX="28px"
        paddingY="12px"
        borderBottom="1px"
        borderColor="gray200"
      >
        {deliveryPostalCode && !isAppliedPostCodeDeliverable
          ? 'Sorry, your address is not in coverage!'
          : 'Your delivery'}
      </Text>

      <Stack paddingX="28px" paddingTop="12px" paddingBottom="12px" spacing="28px">
        {!isAppliedPostCodeDeliverable && deliveryPostalCode && (
          <>
            <Stack spacing="16px">
              <Text variant="text4">
                {
                  "Unfortunately, we're not able to offer delivery or collection for your chosen address:"
                }
              </Text>
              <Flex align="center" gap="4px">
                <ExclamationCircleIcon />

                <Text variant="text4" style={{color: '#9b9b9b'}} data-cs-mask="">
                  {addressDisplay}
                </Text>
              </Flex>
            </Stack>
          </>
        )}

        {softLogoutData.softLogout && (
          <Flex gap="8px">
            <LocationIcon boxSize="20px" style={{color: '#bebebe'}} />
            <Stack spacing="4px" data-cs-mask="">
              {softLogoutData.firstName && <Text variant="text4">{softLogoutData.firstName}</Text>}
              <Text variant="text4">
                {[softLogoutData.email, softLogoutData.postcode].filter(Boolean).join(', ')}
              </Text>
            </Stack>
          </Flex>
        )}
        {!softLogoutData.softLogout && (
          <>
            {hasSavedAddresses && (
              <YourDeliverySavedAddresses
                delimiter={!isAppliedPostCodeDeliverable && deliveryPostalCode}
                setDeliveryAddress={handleSetDeliveryAddress}
              />
            )}
            {customerAddressSelected && (
              <YourDeliverySelectedAddress
                selectedAddress={customerAddressSelected}
                preBook={preBook}
              />
            )}
            {!customerAddressSelected && (
              <EditPostCode
                form={form}
                setPostalCode={setPostalCode}
                checkDeliveryValidity={checkDeliveryValidity}
                postCodeAvailability={postCodeAvailability}
                setPostCodeAvailability={setPostCodeAvailability}
              />
            )}
          </>
        )}
      </Stack>
      {!preBook && deliveryAddressFormExpanded ? (
        <>
          {deliveryAddressFormExpanded === AddressFormType.Add && (
            <AddAddressForm setDeliveryAddress={setDeliveryAddress} />
          )}
          {deliveryAddressFormExpanded === AddressFormType.Edit && (
            <EditAddressForm setDeliveryAddress={setDeliveryAddress} />
          )}
        </>
      ) : null}
      {!preBook && (
        <>
          <Stack
            paddingBottom="28px"
            paddingX="28px"
            spacing="4px"
            className={styles.deliveryNotesContainer}
            position="relative"
          >
            <LoadingOverlay isLoading={deliveryNotesLoading} />
            <Text variant="text3">Delivery notes (Optional)</Text>
            <Box
              as="textarea"
              placeholder="Important notes e.g. information about your delivery..."
              data-test-selector="delivery-notes-textarea"
              maxLength={100}
              padding="8px"
              borderColor="gray200"
              borderRadius="medium"
              fontFamily="body"
              fontSize="sm"
              className={styles.deliveryNotes}
              defaultValue={deliveryNotesValue}
              onInput={handleDeliveryNoteInput}
              onBlur={async (event: React.FocusEvent<HTMLTextAreaElement>) => {
                const note = event.target.value
                try {
                  setDeliveryNotesLoading(true)
                  await updateBasketDeliveryNote(note)
                  setDeliveryNotesLoading(false)
                } catch (error) {
                  console.error('Error setting delivery notes', error)
                  setDeliveryNotesLoading(false)
                }
              }}
              disabled={!basket}
            />
          </Stack>
          <ChangingAddressWarningModal
            onSubmit={changingDeliveryModalSubmitAction ?? noop}
            {...changingDeliveryAddressWarningModal}
          />
        </>
      )}
    </Box>
  )
})
